import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useObservable } from '@cdn/state';
import type { Session } from './session';

const Context = createContext<Session | undefined>(undefined);

export function IdentityProvider({ session, children }: { session: Session; children: ReactNode }) {
	return <Context.Provider value={session}>{children}</Context.Provider>;
}

export function useSession(): Session {
	const session = useContext(Context);
	if (!session) throw new Error('useSession needs an IdentityProvider');
	return useObservable(session);
}

/**
 * Form state that survives signing in again, a provider redirect and a reload. `clear` forgets the draft once
 * the form was sent.
 */
export function useDraft<Value>(form: string, initial: Value): [Value, (value: Value) => void, () => void] {
	const { drafts } = useSession();
	const [value, change] = useState<Value>(() => drafts.read<Value>(form) ?? initial);

	const set = useCallback(
		(next: Value) => {
			change(next);
			drafts.write(form, next);
		},
		[drafts, form]
	);
	const clear = useCallback(() => {
		drafts.clear(form);
		change(initial);
		// The initial value is the form's constant starting point
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drafts, form]);

	return [value, set, clear];
}
